import { z } from "zod";
import type { QueryParams } from "../httpClient";

export const ExpertClaimTypeSchema = z.enum(["freelancer", "agency"]);
export const ExpertClaimStatusSchema = z.enum(["pending", "verified"]);
export const ExpertClaimDecisionSchema = z.enum(["approved", "rejected"]);

export type ExpertClaimType = z.infer<typeof ExpertClaimTypeSchema>;
export type ExpertClaimStatus = z.infer<typeof ExpertClaimStatusSchema>;
export type ExpertClaimDecision = z.infer<typeof ExpertClaimDecisionSchema>;

/** Public claim shape (submit 201 / verify 200, wrapped as `data.claim`). */
export const ExpertClaimSchema = z.object({
    id: z.number(),
    status: ExpertClaimStatusSchema,
    email: z.string(),
    expert: z.object({
        slug: z.string(),
        name: z.string(),
        type: ExpertClaimTypeSchema,
    }),
});

export type ExpertClaim = z.infer<typeof ExpertClaimSchema>;

export const ExpertClaimResponseSchema = z.object({
    claim: ExpertClaimSchema,
});

export type ExpertClaimResponse = z.infer<typeof ExpertClaimResponseSchema>;

export const ResendExpertClaimVerificationResponseSchema = z.object({
    message: z.string(),
});

export type ResendExpertClaimVerificationResponse = z.infer<typeof ResendExpertClaimVerificationResponseSchema>;

export const SubmitFreelancerExpertClaimRequestSchema = z.object({
    first_name: z.string().min(1).max(100),
    last_name: z.string().min(1).max(100),
    role: z.string().min(1).max(100),
    email: z.email().max(255),
    linkedin_url: z.string().url().max(255),
});

export type SubmitFreelancerExpertClaimRequest = z.infer<typeof SubmitFreelancerExpertClaimRequestSchema>;

export const SubmitAgencyExpertClaimRequestSchema = z.object({
    full_name: z.string().min(1).max(200),
    company_name: z.string().min(1).max(200),
    company_email: z.email().max(255),
    email: z.email().max(255),
    linkedin_url: z.string().url().max(255),
});

export type SubmitAgencyExpertClaimRequest = z.infer<typeof SubmitAgencyExpertClaimRequestSchema>;

export type SubmitExpertClaimRequest = SubmitFreelancerExpertClaimRequest | SubmitAgencyExpertClaimRequest;

/** Verification tokens are exactly this long — the single source for clients that pre-validate links. */
export const CLAIM_TOKEN_LENGTH = 64;

export const VerifyExpertClaimRequestSchema = z.object({
    token: z.string().length(CLAIM_TOKEN_LENGTH),
});

export type VerifyExpertClaimRequest = z.infer<typeof VerifyExpertClaimRequestSchema>;

export const ResendExpertClaimVerificationRequestSchema = z.object({
    expert_slug: z.string().max(255),
    email: z.email().max(255),
});

export type ResendExpertClaimVerificationRequest = z.infer<typeof ResendExpertClaimVerificationRequestSchema>;

/** Admin claim shape (list items; single-claim endpoints wrap as `data.claim`). */
export const AdminExpertClaimSchema = z.object({
    id: z.number(),
    type: ExpertClaimTypeSchema,
    status: ExpertClaimStatusSchema,
    claimant_name: z.string(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    full_name: z.string().nullable(),
    email: z.string(),
    company_name: z.string().nullable(),
    company_email: z.string().nullable(),
    linkedin_url: z.string(),
    role: z.string().nullable(),
    decision: ExpertClaimDecisionSchema.nullable(),
    decided_at: z.string().nullable(),
    /** Null when undecided or for system auto-rejections. */
    decided_by: z.object({
        id: z.number(),
        name: z.string(),
    }).nullable(),
    admin_note: z.string().nullable(),
    verified_at: z.string().nullable(),
    /** Set after approval creates (or matches) a user. */
    created_user: z.object({
        id: z.number(),
        email: z.string(),
    }).nullable(),
    expert: z.object({
        id: z.number(),
        slug: z.string(),
        name: z.string(),
        type: ExpertClaimTypeSchema,
        is_claimed: z.boolean(),
    }),
    created_at: z.string(),
    updated_at: z.string(),
});

export type AdminExpertClaim = z.infer<typeof AdminExpertClaimSchema>;

export const AdminExpertClaimResponseSchema = z.object({
    claim: AdminExpertClaimSchema,
});

export type AdminExpertClaimResponse = z.infer<typeof AdminExpertClaimResponseSchema>;

export const ApproveExpertClaimResponseSchema = z.object({
    claim: AdminExpertClaimSchema,
    user_was_existing: z.boolean(),
});

export type ApproveExpertClaimResponse = z.infer<typeof ApproveExpertClaimResponseSchema>;

/** All fields optional — send only what changes; type-specific fields must match the claim's stored type. */
export const AdminUpdateExpertClaimRequestSchema = z.object({
    email: z.email().max(255).optional(),
    linkedin_url: z.string().url().max(255).optional(),
    // Agency claims
    full_name: z.string().min(1).max(200).optional(),
    company_name: z.string().min(1).max(200).optional(),
    company_email: z.email().max(255).optional(),
    // Freelancer claims
    first_name: z.string().min(1).max(100).optional(),
    last_name: z.string().min(1).max(100).optional(),
    role: z.string().min(1).max(100).optional(),
});

export type AdminUpdateExpertClaimRequest = z.infer<typeof AdminUpdateExpertClaimRequestSchema>;

/** `admin_note` must be present; `null` clears the note. */
export const AdminUpdateExpertClaimNoteRequestSchema = z.object({
    admin_note: z.string().max(2000).nullable(),
});

export type AdminUpdateExpertClaimNoteRequest = z.infer<typeof AdminUpdateExpertClaimNoteRequestSchema>;

/** Optional note for approve/reject; only overwrites the stored note when non-null. */
export const AdminDecideExpertClaimRequestSchema = z.object({
    admin_note: z.string().max(2000).nullable().optional(),
});

export type AdminDecideExpertClaimRequest = z.infer<typeof AdminDecideExpertClaimRequestSchema>;

export const ExpertClaimDecisionFilterSchema = z.enum(["approved", "rejected", "any", "none"]);

export type ExpertClaimDecisionFilter = z.infer<typeof ExpertClaimDecisionFilterSchema>;

export interface AdminExpertClaimsListQueryParams extends QueryParams {
    decision?: ExpertClaimDecisionFilter;
    status?: ExpertClaimStatus;
    type?: ExpertClaimType;
    expert_id?: number;
    search?: string;
    limit?: number;
    cursor?: string;
}
